<script>
import storageService from "@/services/storage.service";
import axios from "axios";
import ApplicantLayout from "../../layouts/applicant";
import AppIcon from "@/components/shared/AppIcon.vue";
import { parseNotificationResponse } from "@/helpers/notificationFormatter";

export default {
  name: "ApplicantNotifications",
  components: { ApplicantLayout, AppIcon },
  data() {
    return {
      loading: true,
      loadError: false,
      items: [],
    };
  },
  computed: {
    unreadCount() {
      return this.items.filter((n) => n.unread).length;
    },
  },
  mounted() {
    this.fetchNotifData();
  },
  methods: {
    fetchNotifData() {
      this.loading = true;
      this.loadError = false;
      // read: true marks everything as read on the server; the response still carries
      // the previous read flags, so unread items stay highlighted on this visit.
      axios
        .post("/applications/kick_notif/", {
          token: storageService.getItem("accessToken"),
          read: true,
        })
        .then((response) => {
          this.items = parseNotificationResponse(response.data).items;
          // let the header bell clear its badge
          if (this.$emitter) this.$emitter.emit("notifications:read");
        })
        .catch((error) => {
          console.error(error);
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<template>
  <ApplicantLayout>
    <section class="notif">
      <header class="ecu-page-head">
        <div>
          <h1>Notifications</h1>
          <p class="ecu-page-head__sub">Updates about your membership application.</p>
        </div>
        <span v-if="!loading && unreadCount" class="ecu-chip ecu-chip--orange">
          {{ unreadCount }} new
        </span>
      </header>

      <!-- loading -->
      <div v-if="loading" class="notif__list" aria-busy="true">
        <div v-for="n in 3" :key="n" class="ecu-card notif-card">
          <span class="ecu-skeleton" style="width: 38.4px; height: 38.4px; border-radius: 12px"></span>
          <div style="flex: 1">
            <div class="ecu-skeleton" style="height: 14.4px; width: 40%; margin-bottom: 9.6px"></div>
            <div class="ecu-skeleton" style="height: 11.2px; width: 80%; margin-bottom: 8px"></div>
            <div class="ecu-skeleton" style="height: 9.6px; width: 25%"></div>
          </div>
        </div>
      </div>

      <!-- error -->
      <div v-else-if="loadError" class="ecu-card">
        <div class="ecu-state">
          <span class="ecu-state__icon ecu-state__icon--danger"><AppIcon name="alert" :size="26" /></span>
          <h3>We couldn't load your notifications</h3>
          <p>Please check your connection and try again.</p>
          <button type="button" class="ecu-btn-outline" @click="fetchNotifData">
            <AppIcon name="refresh" :size="14" /> Try again
          </button>
        </div>
      </div>

      <!-- empty -->
      <div v-else-if="!items.length" class="ecu-card">
        <div class="ecu-state">
          <span class="ecu-state__icon"><AppIcon name="bell" :size="26" /></span>
          <h3>You're all caught up</h3>
          <p>When there is an update on your application, it will show up here.</p>
        </div>
      </div>

      <!-- list -->
      <ul v-else class="notif__list">
        <li
          v-for="(n, idx) in items"
          :key="idx"
          class="ecu-card notif-card"
          :class="{ 'notif-card--unread': n.unread }"
          :style="{ animationDelay: `${Math.min(idx, 8) * 40}ms` }"
        >
          <span class="ecu-tile notif-card__icon"><AppIcon name="bell" :size="18" /></span>
          <div class="notif-card__body">
            <h3>{{ n.title }}</h3>
            <p v-if="n.message">{{ n.message }}</p>
            <time v-if="n.date"><AppIcon name="calendar" :size="13" /> {{ n.date }}</time>
          </div>
          <span v-if="n.unread" class="notif-card__dot" title="New" aria-label="New notification"></span>
        </li>
      </ul>
    </section>
  </ApplicantLayout>
</template>

<style scoped>
.notif__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 11.2px;
}
.notif-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 14.4px;
  padding: 16px 19.2px;
  border-radius: 16px;
  overflow: hidden;
  animation: notif-in 0.4s ease both;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.notif-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 14.4px 40px rgba(20, 40, 90, 0.1);
}
.notif-card__icon {
  width: 38.4px;
  height: 38.4px;
  border-radius: 12px;
  background: #f3f4f9;
  color: #7b8194;
}
.notif-card--unread {
  background: linear-gradient(100deg, #fff6ef 0%, #ffffff 60%);
}
.notif-card--unread::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14.4px;
  bottom: 14.4px;
  width: 3.2px;
  border-radius: 0 3.2px 3.2px 0;
  background: linear-gradient(180deg, #ff8a2a, #f2610f);
}
.notif-card--unread .notif-card__icon {
  background: var(--ecu-orange-soft);
  color: var(--ecu-orange);
}
.notif-card__body {
  flex: 1;
  min-width: 0;
}
.notif-card__body h3 {
  margin: 0 0 3.2px;
  font-size: var(--ecu-fs-h3);
  font-weight: var(--ecu-fw-subheading);
  line-height: 1.35;
  color: var(--ecu-navy);
}
.notif-card--unread .notif-card__body h3 {
  font-weight: var(--ecu-fw-subheading);
}
.notif-card__body p {
  margin: 0 0 8px;
  font-size: var(--ecu-fs-body);
  line-height: 1.55;
  color: var(--ecu-muted);
  overflow-wrap: anywhere;
}
.notif-card--unread .notif-card__body p {
  color: var(--ecu-ink);
  font-weight: 600;
}
.notif-card__body time {
  display: inline-flex;
  align-items: center;
  gap: 5.6px;
  font-size: var(--ecu-fs-small);
  color: #8b90a3;
}
.notif-card__dot {
  width: 8px;
  height: 8px;
  margin-top: 6.4px;
  border-radius: 50%;
  background: var(--ecu-orange);
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.15);
  flex-shrink: 0;
}

@keyframes notif-in {
  from { opacity: 0; transform: translateY(6.4px); }
  to   { opacity: 1; transform: none; }
}
@media (max-width: 575px) {
  .notif-card { padding: 12.8px; gap: 11.2px; }
  .notif-card__icon { width: 33.6px; height: 33.6px; }
}
@media (prefers-reduced-motion: reduce) {
  .notif-card { animation: none; transition: none; }
}
</style>